import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const ACTION_LABEL = {
  access: { txt: "🚪 Accesso", bg: "#1565c0" },
  link_success: { txt: "✓ CF collegato", bg: "#1b5e20" },
  link_not_found: { txt: "✗ CF non trovato", bg: "#c62828" },
  link_conflict: { txt: "⚠ CF già in uso", bg: "#ef6c00" },
  link_invalid_format: { txt: "⚠ CF formato errato", bg: "#6a1b9a" },
};

const CICLI = ["I", "P1", "P2", "P3", "P4", "P5", "M1", "M2", "M3", "U"];

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      timeZone: "Europe/Rome",
    });
  } catch { return iso; }
};

export default function AlunniPortalAccessLogPage() {
  const [tab, setTab] = useState("alunni"); // "alunni" | "eventi"

  return (
    <div data-testid="alunni-portal-log-page" style={{ padding: "12px 20px" }}>
      <h1 style={{ fontSize: 18, color: "#4a148c", marginBottom: 10 }}>📋 Log accessi Portale Alunni</h1>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 12, borderBottom: "2px solid #4a148c" }}>
        <button onClick={() => setTab("alunni")} data-testid="tab-alunni"
          style={{ padding: "8px 18px", background: tab === "alunni" ? "#4a148c" : "white",
                   color: tab === "alunni" ? "white" : "#4a148c", border: "1px solid #4a148c",
                   borderBottom: tab === "alunni" ? "none" : "1px solid #4a148c",
                   fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          👥 Lista alunni
        </button>
        <button onClick={() => setTab("eventi")} data-testid="tab-eventi"
          style={{ padding: "8px 18px", background: tab === "eventi" ? "#4a148c" : "white",
                   color: tab === "eventi" ? "white" : "#4a148c", border: "1px solid #4a148c",
                   borderBottom: tab === "eventi" ? "none" : "1px solid #4a148c",
                   fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          📜 Cronologia eventi
        </button>
      </div>

      {tab === "alunni" ? <AlunniTab /> : <EventiTab />}
    </div>
  );
}

// =========== TAB 1: Lista alunni ===========
function AlunniTab() {
  const [data, setData] = useState({ items: [], total: 0, with_access: 0, without_access: 0, anno: null });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [anno, setAnno] = useState("");
  const [ciclo, setCiclo] = useState("");
  const [onlyWith, setOnlyWith] = useState(false);
  // 🆕 Feb 2026: filtri client-side per selezionare Primaria+Media (P1-P5, M1-M3)
  //             e per mostrare solo gli alunni SENZA gmail @canossadiaz.it
  const [gruppoPM, setGruppoPM] = useState(false);
  const [senzaCanossa, setSenzaCanossa] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/alunni-portal/admin/access-log/by-alunno", {
        params: {
          anno: anno || undefined,
          ciclo: ciclo || undefined,
          only_with_access: onlyWith ? 1 : 0,
          q: q || undefined,
        },
      });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anno, ciclo, onlyWith]);

  // 🆕 Feb 2026: filtro client-side per gruppo P+M e per "senza gmail @canossadiaz.it"
  const PM_SET = useMemo(() => new Set(["P1", "P2", "P3", "P4", "P5", "M1", "M2", "M3"]), []);
  const itemsFiltered = useMemo(() => {
    let arr = data.items || [];
    if (gruppoPM) arr = arr.filter(it => PM_SET.has(it.ciclo));
    if (senzaCanossa) {
      arr = arr.filter(it => {
        const em = (it.google_email || "").trim().toLowerCase();
        return !em.endsWith("@canossadiaz.it");
      });
    }
    return arr;
  }, [data.items, gruppoPM, senzaCanossa, PM_SET]);

  // Anni disponibili: anno corrente +/- 5
  const annoCorr = new Date().getFullYear();
  const anniOpts = useMemo(() => {
    const arr = [];
    for (let y = annoCorr + 1; y >= annoCorr - 5; y--) arr.push(y);
    return arr;
  }, [annoCorr]);

  const exportCsv = () => {
    const rows = [["Cod", "Cognome", "Nome", "Ciclo", "Settore", "Classe", "Email Segremat",
                   "Ultimo accesso", "Email login", "N° accessi"]];
    for (const it of itemsFiltered) {
      rows.push([
        it.cod, it.cog, it.nom, it.ciclo, it.settore, it.classe,
        it.google_email_seg || "",
        fmtDT(it.last_access_ts), it.last_access_email || "",
        it.n_access || 0,
      ]);
    }
    const csv = rows.map(r => r.map(c => `"${String(c ?? "")}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `portale-alunni_per-alunno_${anno || data.anno || ""}.csv`;
    a.click();
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <StatBadge label="ALUNNI TOTALI" value={data.total} color="#37474f" />
        <StatBadge label="HANNO FATTO LOGIN" value={data.with_access} color="#1b5e20" />
        <StatBadge label="MAI ACCEDUTI" value={data.without_access} color="#9e9e9e" />
        {(gruppoPM || senzaCanossa) && (
          <StatBadge label="FILTRO ATTIVO" value={itemsFiltered.length} color="#ef6c00" />
        )}
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 10, padding: 12, background: "#fafafa", border: "1px solid #e0e0e0", flexWrap: "wrap" }}>
        <Field label="ANNO">
          <select data-testid="al-anno" value={anno} onChange={e => setAnno(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">{data.anno ? `Corrente (${data.anno})` : "Corrente"}</option>
            {anniOpts.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </Field>
        <Field label="CICLO">
          <div data-testid="al-ciclo-bar" style={{ display: "flex", gap: 4 }}>
            <button onClick={() => { setCiclo(""); setGruppoPM(false); }}
              style={chipStyle(ciclo === "" && !gruppoPM)}>Tutti</button>
            {/* 🆕 Feb 2026: chip "P+M" = Primaria+Media (P1-P5 + M1-M3), filtro client-side */}
            <button onClick={() => { setCiclo(""); setGruppoPM(g => !g); }}
              data-testid="al-ciclo-PM"
              title="Alunni di Primaria (P1-P5) e Media (M1-M3)"
              style={{ ...chipStyle(gruppoPM, "P+M"),
                       background: gruppoPM ? "#0d47a1" : "white",
                       color: gruppoPM ? "white" : "#0d47a1",
                       border: `2px solid #0d47a1`, fontWeight: 800 }}>
              P+M
            </button>
            {CICLI.map(c => (
              <button key={c} onClick={() => { setCiclo(c); setGruppoPM(false); }}
                data-testid={`al-ciclo-${c}`}
                style={chipStyle(ciclo === c, c)}>{c}</button>
            ))}
          </div>
        </Field>
        <Field label="RICERCA">
          <input data-testid="al-q" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Cognome o nome…"
            onKeyDown={e => e.key === "Enter" && load()}
            style={{ width: 220, padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }} />
        </Field>
        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#555", marginBottom: 4 }}>
          <input type="checkbox" data-testid="al-only-with" checked={onlyWith}
            onChange={e => setOnlyWith(e.target.checked)} />
          Solo chi ha fatto login
        </label>
        {/* 🆕 Feb 2026: filtro "senza gmail @canossadiaz.it" */}
        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#c62828", marginBottom: 4, fontWeight: 700 }}
          title="Mostra solo alunni SENZA una gmail @canossadiaz.it collegata">
          <input type="checkbox" data-testid="al-senza-canossa" checked={senzaCanossa}
            onChange={e => setSenzaCanossa(e.target.checked)} />
          Senza gmail @canossadiaz.it
        </label>
        <button data-testid="al-apply" onClick={load}
          style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          🔄 Applica
        </button>
        <button data-testid="al-csv" onClick={exportCsv} disabled={itemsFiltered.length === 0}
          style={{ padding: "6px 14px", background: itemsFiltered.length === 0 ? "#bdbdbd" : "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: itemsFiltered.length === 0 ? "not-allowed" : "pointer", marginLeft: "auto" }}>
          📊 Esporta CSV
        </button>
      </div>

      {err && <div style={{ padding: 8, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", marginBottom: 8, fontSize: 12 }}>{err}</div>}
      {loading && <div style={{ padding: 12, fontSize: 12, color: "#666" }}>Caricamento…</div>}

      <div style={{ overflowX: "auto", border: "1px solid #ddd", maxHeight: "70vh" }}>
        <table data-testid="al-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead style={{ position: "sticky", top: 0 }}>
            <tr style={{ background: "#4a148c", color: "white" }}>
              <th style={th}>COD</th>
              <th style={th}>COGNOME</th>
              <th style={th}>NOME</th>
              <th style={th}>CICLO</th>
              <th style={th}>SETT.</th>
              <th style={th}>CLASSE</th>
              <th style={th}>ULTIMO ACCESSO</th>
              <th style={th}>EMAIL LOGIN</th>
              <th style={th}>GMAIL @canossadiaz.it</th>
              <th style={{ ...th, textAlign: "right" }}>N°</th>
            </tr>
          </thead>
          <tbody>
            {!loading && itemsFiltered.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "#999" }}>Nessun alunno trovato</td></tr>
            )}
            {itemsFiltered.map((it, idx) => (
              <tr key={it.cod} data-testid={`al-row-${it.cod}`}
                style={{ borderBottom: "1px solid #eee", background: idx % 2 ? "#fafafa" : "white" }}>
                <td style={{ ...td, fontFamily: "Consolas, monospace", color: "#888" }}>{it.cod}</td>
                <td style={{ ...td, fontWeight: 700 }}>{it.cog}</td>
                <td style={td}>{it.nom}</td>
                <td style={td}><CicloBadge c={it.ciclo} /></td>
                <td style={{ ...td, fontFamily: "Consolas, monospace", color: "#666" }}>{it.settore || "—"}</td>
                <td style={{ ...td, fontFamily: "Consolas, monospace", color: "#666" }}>{it.classe || "—"}</td>
                <td style={{ ...td, fontFamily: "Consolas, monospace",
                            color: it.last_access_ts ? "#1b5e20" : "#999",
                            fontWeight: it.last_access_ts ? 700 : 400,
                            whiteSpace: "nowrap" }}>
                  {it.last_access_ts ? fmtDT(it.last_access_ts) : "— mai —"}
                </td>
                <td style={{ ...td, fontFamily: "Consolas, monospace", color: "#666" }}>
                  {it.last_access_email || "—"}
                </td>
                <td style={{ ...td, padding: "4px 6px" }}>
                  <GoogleEmailInline cod={it.cod} value={it.google_email || ""}
                    onUpdate={(cod, em) => {
                      setData(d => ({ ...d, items: d.items.map(x => x.cod === cod ? { ...x, google_email: em } : x) }));
                    }} testid={`al-gmail-${it.cod}`} />
                </td>
                <td style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace",
                            fontWeight: 700, color: it.n_access > 0 ? "#1b5e20" : "#999" }}>
                  {it.n_access || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =========== TAB 2: Cronologia eventi (preserva l'esistente) ===========
function EventiTab() {
  const [data, setData] = useState({ items: [], total: 0, by_action: {} });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");
  const [days, setDays] = useState(90);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/alunni-portal/admin/access-log", {
        params: { action: action || undefined, status: status || undefined, q: q || undefined, days, limit: 500 },
      });
      setData(r.data || { items: [], total: 0, by_action: {} });
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, status, days]);

  const by = data.by_action || {};
  const stats = useMemo(() => ({
    accessi: by.access || 0,
    successi: by.link_success || 0,
    errori_cf: (by.link_not_found || 0) + (by.link_conflict || 0) + (by.link_invalid_format || 0),
  }), [by]);

  const exportCsv = () => {
    const rows = [["Data/Ora", "Azione", "Stato", "Email", "Nome", "Codice Fiscale", "Cod. Alunno", "IP", "Dettaglio"]];
    for (const it of data.items) {
      rows.push([fmtDT(it.ts), it.action, it.status, it.email || "", it.name || "",
                 it.codice_fiscale || "", it.alunno_cod || "", it.ip || "", (it.detail || "").replace(/"/g, "''")]);
    }
    const csv = rows.map(r => r.map(c => `"${String(c ?? "")}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `portale-alunni-log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <StatBadge label="ACCESSI" value={stats.accessi} color="#1565c0" />
        <StatBadge label="CF COLLEGATI" value={stats.successi} color="#1b5e20" />
        <StatBadge label="ERRORI CF" value={stats.errori_cf} color="#c62828" />
        <StatBadge label="TOTALE EVENTI" value={data.total} color="#37474f" />
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 10, padding: 12, background: "#fafafa", border: "1px solid #e0e0e0", flexWrap: "wrap" }}>
        <Field label="AZIONE">
          <select data-testid="log-action" value={action} onChange={e => setAction(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">Tutte</option>
            <option value="access">🚪 Accesso</option>
            <option value="link_success">✓ CF collegato</option>
            <option value="link_not_found">✗ CF non trovato</option>
            <option value="link_conflict">⚠ CF già in uso</option>
            <option value="link_invalid_format">⚠ CF formato errato</option>
          </select>
        </Field>
        <Field label="ESITO">
          <select data-testid="log-status" value={status} onChange={e => setStatus(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">Tutti</option>
            <option value="ok">OK</option>
            <option value="linked">Linked (al login)</option>
            <option value="not_linked">Non collegato</option>
            <option value="error">Errore</option>
          </select>
        </Field>
        <Field label="FINESTRA (gg)">
          <select data-testid="log-days" value={days} onChange={e => setDays(Number(e.target.value))}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="1">Oggi</option>
            <option value="7">7 giorni</option>
            <option value="30">30 giorni</option>
            <option value="90">90 giorni</option>
            <option value="180">6 mesi</option>
            <option value="365">1 anno</option>
          </select>
        </Field>
        <Field label="RICERCA">
          <input data-testid="log-q" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Email, nome, codice fiscale…"
            onKeyDown={e => e.key === "Enter" && load()}
            style={{ width: 280, padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }} />
        </Field>
        <button data-testid="log-apply" onClick={load}
          style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          🔄 Applica
        </button>
        <button data-testid="log-csv" onClick={exportCsv} disabled={data.items.length === 0}
          style={{ padding: "6px 14px", background: data.items.length === 0 ? "#bdbdbd" : "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: data.items.length === 0 ? "not-allowed" : "pointer", marginLeft: "auto" }}>
          📊 Esporta CSV
        </button>
      </div>

      {err && <div style={{ padding: 8, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", marginBottom: 8, fontSize: 12 }}>{err}</div>}
      {loading && <div style={{ padding: 12, fontSize: 12, color: "#666" }}>Caricamento…</div>}

      <div style={{ overflowX: "auto", border: "1px solid #ddd" }}>
        <table data-testid="log-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#4a148c", color: "white" }}>
              <th style={th}>DATA/ORA</th>
              <th style={th}>AZIONE</th>
              <th style={th}>EMAIL GOOGLE</th>
              <th style={th}>NOME</th>
              <th style={th}>CODICE FISCALE</th>
              <th style={th}>COD. ALUNNO</th>
              <th style={th}>IP</th>
              <th style={th}>DETTAGLIO</th>
            </tr>
          </thead>
          <tbody>
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: "#999" }}>Nessun evento</td></tr>
            )}
            {data.items.map((it, idx) => {
              const cfg = ACTION_LABEL[it.action] || { txt: it.action, bg: "#555" };
              return (
                <tr key={idx} style={{ borderBottom: "1px solid #eee", background: idx % 2 ? "#fafafa" : "white" }}>
                  <td style={{ ...td, fontFamily: "Consolas, monospace", whiteSpace: "nowrap" }}>{fmtDT(it.ts)}</td>
                  <td style={td}>
                    <span style={{ padding: "2px 7px", background: cfg.bg, color: "white", fontSize: 10, fontWeight: 700, borderRadius: 2, whiteSpace: "nowrap" }}>{cfg.txt}</span>
                  </td>
                  <td style={{ ...td, fontFamily: "Consolas, monospace" }}>{it.email || "—"}</td>
                  <td style={td}>{it.name || "—"}</td>
                  <td style={{ ...td, fontFamily: "Consolas, monospace", letterSpacing: 0.5 }}>{it.codice_fiscale || "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace" }}>{it.alunno_cod || "—"}</td>
                  <td style={{ ...td, fontFamily: "Consolas, monospace", color: "#888" }}>{it.ip || "—"}</td>
                  <td style={{ ...td, fontSize: 11, color: "#555" }}>{it.detail || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <div style={{ background: color, color: "white", padding: "8px 14px", borderRadius: 4, minWidth: 120 }}>
      <div style={{ fontSize: 10, opacity: 0.85, fontWeight: 600, letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "Consolas, monospace" }}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#555", marginBottom: 3, letterSpacing: 0.5 }}>{label}</div>
      {children}
    </div>
  );
}

function CicloBadge({ c }) {
  if (!c) return <span style={{ color: "#bbb", fontSize: 11 }}>—</span>;
  const colorMap = {
    I: "#9c27b0",
    P1: "#1565c0", P2: "#1976d2", P3: "#1e88e5", P4: "#2196f3", P5: "#42a5f5",
    M1: "#2e7d32", M2: "#388e3c", M3: "#43a047",
    U: "#616161",
  };
  const bg = colorMap[c] || "#666";
  return (
    <span style={{ padding: "2px 8px", background: bg, color: "white",
                   fontSize: 10, fontWeight: 700, borderRadius: 2,
                   fontFamily: "Consolas, monospace" }}>
      {c}
    </span>
  );
}

const chipStyle = (active, c) => ({
  padding: "4px 10px",
  background: active ? (c ? "#4a148c" : "#37474f") : "white",
  color: active ? "white" : "#555",
  border: `1px solid ${active ? "#4a148c" : "#bbb"}`,
  fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2,
  fontFamily: c ? "Consolas, monospace" : "inherit",
});

const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, borderBottom: "2px solid #311b92" };
const td = { padding: "6px 10px", verticalAlign: "top" };

/**
 * Editor inline per la Google email (campo `alunni.google_email`).
 * Salva via PUT /scuola-curriculum/{cod}/google-email su tutti gli anni dell'alunno.
 */
function GoogleEmailInline({ cod, value, onUpdate, testid }) {
  const [local, setLocal] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setLocal(value || ""); setDirty(false); }, [value]);

  const isCanossa = local.toLowerCase().endsWith("@canossadiaz.it");
  const hasError = local && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(local);
  const baseColor = !local ? "#aaa" : (isCanossa ? "#1b5e20" : "#c62828");

  const save = async () => {
    if (!dirty || hasError) return;
    setSaving(true); setErr("");
    try {
      await api.put(`/scuola-curriculum/${cod}/google-email`, { google_email: local.trim() });
      setDirty(false);
      if (onUpdate) onUpdate(cod, local.trim());
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <input
        data-testid={testid}
        type="email"
        value={local}
        placeholder="nome@canossadiaz.it"
        onChange={(e) => { setLocal(e.target.value); setDirty(e.target.value !== (value || "")); setErr(""); }}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } if (e.key === "Escape") { setLocal(value || ""); setDirty(false); e.target.blur(); } }}
        disabled={saving}
        style={{
          width: "100%", minWidth: 220, padding: "4px 6px", fontSize: 11,
          fontFamily: "Consolas, monospace",
          color: baseColor, border: `1px solid ${hasError ? "#c62828" : (dirty ? "#ffa000" : "#ccc")}`,
          background: hasError ? "#ffebee" : (dirty ? "#fffde7" : "white"),
          borderRadius: 2,
        }}
        title={err || (hasError ? "Email non valida" : (isCanossa ? "Email Canossa ✓" : (local ? "Non è una mail @canossadiaz.it" : "Vuoto")))}
      />
      {saving && <span style={{ fontSize: 10, color: "#888" }}>…</span>}
    </div>
  );
}

